import { Link } from "react-router";

import { Card } from "../components/ui";
import { useConfig } from "../lib/config";
import { brand } from "../theme/brand";

export function HomePage() {
  return (
    <div className="flex flex-col gap-8">
      <section className="rounded-xl bg-brand-600 px-6 py-10 text-white sm:px-10">
        <h1 className="text-3xl font-bold sm:text-4xl">{brand.portalName}</h1>
        <p className="mt-3 max-w-2xl text-lg text-brand-50">
          Send print orders to Bill's, follow their progress, and pick up the files Bill's sends
          you, all in one place.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/register"
            className="inline-flex min-h-11 items-center rounded-md bg-white px-5 font-semibold text-brand-700 hover:bg-brand-50"
          >
            Create an account
          </Link>
          <Link
            to="/login"
            className="inline-flex min-h-11 items-center rounded-md border border-white px-5 font-semibold text-white hover:bg-white/10"
          >
            Sign in
          </Link>
        </div>
      </section>
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <h2 className="text-lg font-bold">Orders TO Bill's</h2>
          <p className="mt-2 text-ink-muted">
            Upload your files, choose sizes and delivery, and request a due date.
          </p>
        </Card>
        <Card>
          <h2 className="text-lg font-bold">Track your work</h2>
          <p className="mt-2 text-ink-muted">
            See when your order is received, in production, ready, and completed.
          </p>
        </Card>
        <Card>
          <h2 className="text-lg font-bold">Files FROM Bill's</h2>
          <p className="mt-2 text-ink-muted">
            Download scans and finished files that Bill's sends you, securely.
          </p>
        </Card>
      </div>
    </div>
  );
}

// General-purpose wording describing how the portal works. Review with Bill's Blueprint's
// legal advisor and update LAST_UPDATED whenever the text changes.
const LAST_UPDATED = "October 20, 2026";

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article className="mx-auto max-w-3xl">
      <h1 className="text-2xl font-bold">{title}</h1>
      <p className="mb-4 text-sm text-ink-muted">Last updated {LAST_UPDATED}</p>
      <Card>
        <div className="space-y-6 text-ink">{children}</div>
      </Card>
    </article>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-bold">{title}</h2>
      {children}
    </section>
  );
}

function ContactBlock() {
  const { data } = useConfig();
  if (!data) return null;
  return (
    <address className="not-italic">
      Bill's Blueprint
      <br />
      {data.contact.address}
      <br />
      Phone:{" "}
      <a className="text-brand-700 underline" href={`tel:${data.contact.phone}`}>
        {data.contact.phone}
      </a>
      <br />
      Email:{" "}
      <a className="text-brand-700 underline" href={`mailto:${data.contact.email}`}>
        {data.contact.email}
      </a>
    </address>
  );
}

const List = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc space-y-1 pl-6">{children}</ul>
);

export function PrivacyPage() {
  return (
    <LegalShell title="Privacy Policy">
      <p>
        This Privacy Policy explains how Bill's Blueprint ("Bill's", "we", "us") collects, uses and
        protects information when you use the Bill's Blueprint Customer Portal (the "Portal"). By
        using the Portal, you agree to the practices described here.
      </p>

      <Section title="Information we collect">
        <List>
          <li>
            <strong>Account information:</strong> your name, email address, phone number and
            password (stored only in a securely scrambled form we cannot read).
          </li>
          <li>
            <strong>Order information:</strong> contact and delivery addresses, project names,
            printing instructions, due dates, payment method selection and any notes you provide.
          </li>
          <li>
            <strong>Files:</strong> documents you upload for printing and files we send to you.
          </li>
          <li>
            <strong>Company information:</strong> if you belong to a company account, your
            membership and role in that company.
          </li>
          <li>
            <strong>Technical information:</strong> sign-in times, IP address, browser type and a
            record of important actions (such as order changes and file downloads), kept for
            security and record-keeping.
          </li>
        </List>
        <p>
          We do not collect payment card numbers through the Portal. Payment is arranged directly
          with Bill's.
        </p>
      </Section>

      <Section title="How we use your information">
        <List>
          <li>To process, produce and deliver your print orders.</li>
          <li>To send you files and let you download them securely.</li>
          <li>
            To contact you about your orders, such as order confirmations, due date changes, and
            when your order is ready or complete.
          </li>
          <li>To manage your account and company access, and to keep the Portal secure.</li>
          <li>To keep accurate business records and meet legal obligations.</li>
        </List>
        <p>We do not sell your personal information or use it for advertising.</p>
      </Section>

      <Section title="Who can see your information">
        <List>
          <li>
            <strong>Bill's staff</strong> who need it to handle your orders and files.
          </li>
          <li>
            <strong>Members of your company account</strong> can see orders and files placed for
            that company. Orders you place for yourself stay private to you.
          </li>
          <li>
            <strong>Service providers</strong> who help us run the Portal, such as hosting, email
            delivery and security scanning. They may use your information only to provide those
            services to us.
          </li>
          <li>
            <strong>Others when required by law</strong>, or to protect the rights, property or
            safety of Bill's, our customers or others.
          </li>
        </List>
      </Section>

      <Section title="How long we keep information">
        <List>
          <li>
            Files you upload for an order are deleted from the Portal 90 days after the order is
            completed.
          </li>
          <li>Files Bill's sends you are deleted 45 days after they are made available.</li>
          <li>
            Order details, account information and activity records are kept as long as needed for
            business, accounting and legal purposes.
          </li>
          <li>
            Deleted files may remain in secure backups for a limited time before the backups are
            replaced.
          </li>
        </List>
      </Section>

      <Section title="Cookies">
        <p>
          The Portal uses only the cookies needed to keep you signed in and to protect your account
          against misuse. We do not use advertising or tracking cookies.
        </p>
      </Section>

      <Section title="Security">
        <p>
          We use reasonable safeguards to protect your information, including encrypted connections,
          malware scanning of uploaded files, access controls and two-step verification for staff.
          No system is completely secure, so please keep your password private and let us know right
          away if you suspect your account has been misused.
        </p>
      </Section>

      <Section title="Your choices">
        <p>
          You can view and update your contact details on the My account page. To ask about the
          information we hold about you, request corrections, or close your account, contact us
          using the details below. Some information may need to be kept for business or legal
          reasons.
        </p>
      </Section>

      <Section title="Children">
        <p>The Portal is intended for business use and is not directed to children under 13.</p>
      </Section>

      <Section title="Changes to this policy">
        <p>
          We may update this policy from time to time. The date at the top of this page shows when
          it was last changed. Continuing to use the Portal after a change means you accept the
          updated policy.
        </p>
      </Section>

      <Section title="Contact us">
        <ContactBlock />
      </Section>
    </LegalShell>
  );
}

export function TermsPage() {
  return (
    <LegalShell title="Terms of Use">
      <p>
        These Terms of Use ("Terms") govern your use of the Bill's Blueprint Customer Portal (the
        "Portal"). By creating an account or using the Portal, you agree to these Terms. If you do
        not agree, please do not use the Portal.
      </p>

      <Section title="Your account">
        <List>
          <li>Each person must have their own account. Do not share your sign-in details.</li>
          <li>Provide accurate information and keep your contact details up to date.</li>
          <li>
            You are responsible for activity under your account. Tell us right away if you believe
            someone else has used it.
          </li>
          <li>
            Company administrators are responsible for approving only people who work with their
            company, and for removing access when someone leaves.
          </li>
        </List>
      </Section>

      <Section title="Orders">
        <List>
          <li>
            Submitting an order is a request for printing services. Bill's may contact you to
            clarify details before starting work.
          </li>
          <li>
            Requested due dates are not guaranteed until Bill's confirms them. If Bill's proposes a
            different date, it is confirmed only after you accept it.
          </li>
          <li>
            Once an order is submitted, changes must be requested through the Portal or by
            contacting Bill's. Changes may affect timing and cost.
          </li>
          <li>
            Pricing, payment and delivery are handled according to your arrangement with Bill's.
            Choosing a payment method in the Portal does not process a payment.
          </li>
        </List>
      </Section>

      <Section title="Your files">
        <List>
          <li>
            You keep ownership of the files you upload. You give Bill's permission to store, copy
            and print them as needed to complete your order.
          </li>
          <li>
            Upload only files you have the right to use and print. You are responsible for ensuring
            your files do not infringe anyone else's rights.
          </li>
          <li>
            Files are checked for viruses. Files that fail security checks are removed and cannot be
            used.
          </li>
          <li>
            Files are kept only for a limited time (see the Privacy Policy). Please keep your own
            copies of important files.
          </li>
        </List>
      </Section>

      <Section title="Acceptable use">
        <p>You agree not to:</p>
        <List>
          <li>Upload harmful software or try to interfere with the Portal or its security.</li>
          <li>Try to access accounts, orders or files that do not belong to you.</li>
          <li>Use the Portal for anything unlawful or unrelated to business with Bill's.</li>
        </List>
        <p>We may suspend or close accounts that violate these Terms.</p>
      </Section>

      <Section title="Availability">
        <p>
          We work to keep the Portal available and accurate, but it is provided "as is" and may
          occasionally be unavailable for maintenance or reasons outside our control. If you have an
          urgent order, please contact us directly.
        </p>
      </Section>

      <Section title="Limitation of liability">
        <p>
          To the extent allowed by law, Bill's is not liable for indirect or consequential losses
          arising from use of the Portal. Nothing in these Terms limits any rights you have that
          cannot be limited by law.
        </p>
      </Section>

      <Section title="Changes to these Terms">
        <p>
          We may update these Terms from time to time. The date at the top of this page shows when
          they were last changed. Continuing to use the Portal after a change means you accept the
          updated Terms.
        </p>
      </Section>

      <Section title="Governing law">
        <p>These Terms are governed by the laws of the State of Washington.</p>
      </Section>

      <Section title="Contact us">
        <ContactBlock />
      </Section>
    </LegalShell>
  );
}

export function NotFoundPage() {
  return (
    <div className="mx-auto max-w-md text-center">
      <h1 className="text-2xl font-bold">Page not found</h1>
      <p className="mt-2 text-ink-muted">We could not find that page.</p>
      <Link to="/" className="mt-4 inline-block text-brand-700 underline">
        Go to the home page
      </Link>
    </div>
  );
}
